import useCheckCartConflict from '@hooks/useCheckCartConflict';
import { setDeliveryMode, deleteSlotInCart } from '@api/interfaces/cartApi';
import trackPickUpInStoreSelection from '@helpers/analyticsHelpers/trackPickUpInStoreSelection';
import logger from '@logger';
import useStore from '@hooks/useStore';
import useCart from '@hooks/useCart';
import useAbortController from '@hooks/useAbortController';
import { useMemo, useState } from 'react';
import paths from '@constants/paths';
import { usePathname } from 'next/navigation';

interface Props {
  afterSubmit?: () => void;
}

const useFinishPickUpSubmission = ({ afterSubmit }: Props) => {
  const pathname = usePathname();
  const isCheckoutPath = useMemo(() => pathname === paths.CHECKOUT, [pathname]);
  const { refreshCart } = useCart();
  const checkCartConflict = useCheckCartConflict();
  const { getSignal } = useAbortController();
  const { store, refreshStore } = useStore();
  const [isLoading, setIsLoading] = useState(false);

  const handleStoreSwitch = async (deliveryStoreId: string) => {
    const { activateStore } = await import('@api/interfaces/storeApi');
    const response = await activateStore(deliveryStoreId, true, false, getSignal());
    await refreshStore(response.data);
    await deleteSlotInCart(getSignal());
  };

  const finishPickUpSubmission = async (deliveryStoreId: string, errorCallback: () => void) => {
    try {
      setIsLoading(true);
      await checkCartConflict({ storeId: deliveryStoreId });
      await setDeliveryMode('pickUpInStore', deliveryStoreId);
      if (store?.storeId !== deliveryStoreId || !store?.activelySelected) {
        await handleStoreSwitch(deliveryStoreId);
      }
      await refreshCart();
      afterSubmit?.();
      trackPickUpInStoreSelection(deliveryStoreId, isCheckoutPath);
    } catch (e) {
      errorCallback();
      if (e instanceof Error) {
        logger.error({ error: 'Could not finish pickup submit', message: e.message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { finishPickUpSubmission, isLoading };
};

export default useFinishPickUpSubmission;
